import type { InventoryListByIdAPI } from '@openx/types/inventoryList.ts';
import {
  type AppInventoryOptionType,
  type AppInventoryState,
  AppInventoryVersion,
  type AppInventoryVersionType,
} from '@openx/types/targeting/appInventory.ts';
import { type InstanceProps, InventoryContentOption } from '@openx/types/targeting/inventoryAndContent.ts';
import { Criteria } from '@openx/types/targeting/targetingValuesTypes.ts';
import { noop } from '@openx/utils/noop.ts';
import { Fragment, type JSX, useMemo } from 'react';
import { getChipLabelVisibilityFlags } from '../../../../utils/hooks/getChipLabelVisibilityFlags.ts';
import { useParameterRemove } from '../../../../utils/hooks/useParameterRemove.ts';
import { useTargetingChildDisabledState } from '../../../../utils/hooks/useTargetingChildDisabledState.ts';
import { getInventoriesWithSettings } from '../../../InventoryAndContent/InventoryAndContentItems/utils.ts';
import type { SetInventoryListItemsDispatch } from '../../../InventoryAndContent/InventoryAndContentTopBox/constants.ts';
import { resolveAppInventoryOperator } from '../../../shared/AppInventory/utils/resolveAppInventoryOperator.ts';
import type { InvalidItemsError } from '../../../shared/BulkAdd/types.ts';
import { DimensionChipLine } from '../../../shared/DimensionChip/DimensionChipLine.tsx';
import { appInventoryFormPreviewSectionRenderer } from '../../../shared/InventoryAndContent/components/appInventoryFormPreviewSectionRenderer.tsx';
import { useAppInventoryAllowed } from '../../../shared/InventoryAndContent/featureFlags.ts';
import {
  TargetingDropdownSection,
  type TargetingDropdownSectionProps,
} from '../../../shared/ReadOnlyTargetingDropdown/TargetingDropdownSection.tsx';
import type { TargetingItemsProps } from '../../../types.ts';
import { getInventoryAndContentOptions } from '../../hooks/getInventoryAndContentOptions.tsx';
import { useContentOptions } from '../../hooks/useContentOptions.ts';

interface ContentItemsProps extends TargetingItemsProps<AppInventoryState> {
  inputInstanceValue?: InstanceProps | null;
  appBundleIdDuplicateErrors: InvalidItemsError | null;
  setInventoryListItems?: SetInventoryListItemsDispatch;
  inventoryListItems?: InventoryListByIdAPI['values'];
  inventoryVersion?: AppInventoryVersionType;
  onRemoveAppName: () => void;
  onRemoveBundleIdManual: () => void;
  onRemoveBundleIdInventoryList: () => void;
}

export const AppInventoryFormPreviewItems = ({
  targetingParams,
  dispatchInventory = noop,
  readonly,
  accountUid = '',
  appBundleIdDuplicateErrors,
  setInventoryListItems = noop,
  inventoryListItems = [],
  onOpenDrawerClick = noop,
  onRemove = noop,
  onRemoveParameter,
  inventoryVersion,
  onRemoveAppName,
  onRemoveBundleIdManual,
  onRemoveBundleIdInventoryList,
  field,
}: ContentItemsProps &
  Omit<TargetingDropdownSectionProps, 'isDisabled' | 'disabledReasonMessage'> & {
    onRemoveParameter?: (option: AppInventoryOptionType) => void;
  }) => {
  const { getDisabledStateForOption } = useTargetingChildDisabledState(field);

  const isAppInventoryAllowed = useAppInventoryAllowed();
  const inventoriesWithSettings = getInventoriesWithSettings(targetingParams);
  const { getOptionLabel, options } = useContentOptions({
    isBulk: false,
  });

  const switchState = resolveAppInventoryOperator(targetingParams) === Criteria.ANY;

  const hasAppInventoryData =
    isAppInventoryAllowed &&
    (inventoriesWithSettings.includes(InventoryContentOption.APP_GROUP) ||
      inventoriesWithSettings.includes(InventoryContentOption.APP_BUNDLE_ID));

  const appBundleIdState = getDisabledStateForOption(InventoryContentOption.APP_BUNDLE_ID);
  const appGroupState = getDisabledStateForOption(InventoryContentOption.APP_GROUP);

  const inventoryAndContent = getInventoryAndContentOptions({
    dispatchInventory,
    readonly,
    targetingParams,
    accountUid,
    appBundleIdDuplicateErrors,
    setInventoryListItems,
    inventoryListItems,
    switchState,
    getDisabledReasonMessage: option => getDisabledStateForOption(option).errorReasonMessage,
  });

  const removeHandler = useParameterRemove<AppInventoryOptionType>(onRemoveParameter, onRemove);

  const availableOptions = useMemo(
    () =>
      options.reduce(
        (acc, key) => {
          if (key in inventoryAndContent) acc.push([key, inventoryAndContent[key]]);
          return acc;
        },
        [] as Array<[string, (isNotLastItem: boolean, showDimensionChipLine: boolean) => JSX.Element | null]>
      ),
    [options, inventoryAndContent]
  );

  const configuredKeySet = useMemo(() => new Set(inventoriesWithSettings), [inventoriesWithSettings]);

  const labelVisibilityFlags = useMemo(
    () =>
      getChipLabelVisibilityFlags(
        availableOptions.map(([key]) => key),
        key => configuredKeySet.has(key)
      ),
    [availableOptions, configuredKeySet]
  );

  const isCombinedAppInventorySectionDisabled = appBundleIdState.isDisabled || appGroupState.isDisabled;

  return (
    <>
      {availableOptions.map(([key, component], index) => {
        const isLastItem = index === availableOptions.length - 1;
        const showChipLabel = labelVisibilityFlags[index] ?? false;

        const isAppGroupWithCombinedSection = key === InventoryContentOption.APP_GROUP && hasAppInventoryData;
        const isSectionEmpty = isAppGroupWithCombinedSection
          ? !configuredKeySet.has(InventoryContentOption.APP_GROUP) &&
            !configuredKeySet.has(InventoryContentOption.APP_BUNDLE_ID)
          : !configuredKeySet.has(key);

        if (
          (inventoryVersion === AppInventoryVersion.APP_INVENTORY_ONLY_V2 && isAppInventoryAllowed) ||
          isAppGroupWithCombinedSection
        )
          return (
            <Fragment key={key}>
              {appInventoryFormPreviewSectionRenderer({
                field,
                targetingParams,
                dispatchInventory,
                switchState,
                accountUid,
                inventoryListItems,
                setInventoryListItems,
                inventoryVersion,
                isDisabled: isCombinedAppInventorySectionDisabled,
                onOpenDrawerClick,
                onRemoveAppName,
                onRemoveBundleIdManual,
                onRemoveBundleIdInventoryList,
              })}
            </Fragment>
          );

        const { isDisabled: isSectionDisabled, disabledReasonMessage: sectionDisabledReasonMessage } =
          getDisabledStateForOption(key);

        return (
          <Fragment key={key}>
            <TargetingDropdownSection
              name={getOptionLabel(key)}
              isEmpty={isSectionEmpty}
              onOpenDrawerClick={() => onOpenDrawerClick(key)}
              onRemove={removeHandler(key as AppInventoryOptionType)}
              isDisabled={isSectionDisabled}
              disabledReasonMessage={sectionDisabledReasonMessage}
              hasOptionsSelected={!isSectionEmpty}
            >
              {component(!isLastItem, false)}
            </TargetingDropdownSection>

            {!isLastItem && <DimensionChipLine switchState={switchState} showLabel={showChipLabel} />}
          </Fragment>
        );
      })}
    </>
  );
};
